
/**
 * Module dependencies.
 */

import { ProductTypology } from 'src/types/products';
import { availableFiltersKeys } from 'src/core/collections-config/filters';
import { getKeyFromObject } from 'src/core/utils/objects';
import {
  typologies,
  typologiesIconsSmall,
  typologiesValues
} from 'src/core/collections-config/typologies';

import { useFiltersList } from 'src/context/filters-list/context';
import { useTranslate } from 'src/core/utils/translator';
import FilterDisclosure from 'src/components/filters/filter-disclosure';
import FilterItem from 'src/components/filters/filter-item';
import FilterItemLabel from 'src/components/filters/filter-item-label';
import React, { ReactElement, useMemo } from 'react';
import filter from 'lodash/filter';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import keys from 'lodash/keys';
import sortBy from 'lodash/sortBy';
import split from 'lodash/split';

/**
 * Filter name.
 */

const filterName = availableFiltersKeys.typology;

/**
 * `Props` type.
 */

type Props = {
  filtersList?: ProductTypology[]
};

/**
 * `TypologyFilter` component.
 */

const TypologyFilter = ({ filtersList }: Props): ReactElement => {
  const { translate } = useTranslate();
  const { filters } = useFiltersList();
  const hasFilter = keys(filters).includes(filterName);
  const selectedFilters = hasFilter ? split(filters[filterName], ',').map(typology => {
    const key = getKeyFromObject(typologiesValues, typology);

    return translate(`collections:typologies.${key}`);
  }) : [];

  const typologiesList = useMemo(() => {
    const orderedTypologies = sortBy(typologies, color => {
      return translate(`collections:typologies.${color}`);
    });

    if (isEmpty(filtersList)) {
      return orderedTypologies;
    }

    return filter(orderedTypologies, typology => {
      return includes(filtersList, typologiesValues[typology]);
    });
  }, [filtersList, translate]);

  return (
    <FilterDisclosure
      name={filterName}
      selectedFilters={selectedFilters}
      title={translate('common:labels.typology')}
    >
      {typologiesList.map((typology: ProductTypology) => (
        <FilterItem
          key={typology}
          label={(
            <FilterItemLabel
              title={translate(`collections:typologies.${typology}`)}
              type={'icon'}
              value={typologiesIconsSmall[typology]}
            />
          )}
          name={filterName}
          value={typologiesValues[typology]}
        />
      ))}
    </FilterDisclosure>
  );
};

/**
 * Export `TypologyFilter` component.
 */

export default TypologyFilter;
